/**
 * PWA helpers — service-worker registration, install prompt, and push subscription.
 *
 * Exports:
 *   - `registerServiceWorker()` — call once at app startup.
 *   - `useInstallPrompt()` — React hook returning `{canInstall, promptInstall, installed}`.
 *   - `subscribeToPush({vapidKey, api})` — requests permission and subscribes.
 *   - `unsubscribeFromPush({api})` — removes the active subscription.
 *   - `getPushSubscriptionState()` — `{supported, permission, subscribed}`.
 */

import { useEffect, useState, useCallback } from 'react';

let installEvent = null;

export function registerServiceWorker() {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {
      // swallow — SW is progressive enhancement
    });
  });

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    installEvent = e;
    window.dispatchEvent(new CustomEvent('pwa:install-available'));
  });

  window.addEventListener('appinstalled', () => {
    installEvent = null;
    window.dispatchEvent(new CustomEvent('pwa:installed'));
  });
}

export function useInstallPrompt() {
  const [canInstall, setCanInstall] = useState(!!installEvent);
  const [installed, setInstalled] = useState(
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(display-mode: standalone)').matches
  );

  useEffect(() => {
    const onAvail = () => setCanInstall(true);
    const onInstalled = () => { setCanInstall(false); setInstalled(true); };
    window.addEventListener('pwa:install-available', onAvail);
    window.addEventListener('pwa:installed', onInstalled);
    return () => {
      window.removeEventListener('pwa:install-available', onAvail);
      window.removeEventListener('pwa:installed', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installEvent) return { outcome: 'unavailable' };
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') {
      installEvent = null;
      setCanInstall(false);
    }
    return choice;
  }, []);

  return { canInstall, installed, promptInstall };
}

// ---------- Push helpers ----------

function urlBase64ToUint8Array(b64) {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function getPushSubscriptionState() {
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
  if (!supported) return { supported: false, permission: 'unsupported', subscribed: false };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return { supported: true, permission: Notification.permission, subscribed: !!sub };
}

export async function subscribeToPush({ vapidKey, api }) {
  if (!vapidKey) throw new Error('VAPID public key is required');
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') throw new Error('Notification permission denied');

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }
  await api.subscribePush(sub.toJSON());
  return sub;
}

export async function unsubscribeFromPush({ api }) {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return false;
  try {
    await api.unsubscribePush(sub.endpoint);
  } catch (e) {
    // Swallow — still unsubscribe locally
  }
  return sub.unsubscribe();
}
